
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ExchangeMetricsCard from "@/components/monitoring/ExchangeMetricsCard";
import GlobalMetricsCard from "@/components/monitoring/GlobalMetricsCard";
import StrategyTable from "@/components/monitoring/StrategyTable";
import { useStrategySummary } from "@/hooks/useStrategySummary";
import Header from "@/components/Header";
import AdminRoute from "@/components/auth/AdminRoute";

const MonitoringDashboard = () => {
  const { toast } = useToast();
  const { 
    data, 
    loading, 
    globalMetrics, 
    exchangeMetrics,
    error 
  } = useStrategySummary();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error loading data",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  return (
    <AdminRoute>
      <div className="min-h-screen bg-gradient-to-b from-[#E7F0FD] to-white">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#243949] mb-2">Strategy Monitoring Dashboard</h1>
            <p className="text-[#517FA4]">Track performance metrics across all exchanges and strategies</p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#517FA4]"></div>
            </div>
          ) : (
            <>
              <GlobalMetricsCard metrics={globalMetrics} />
              
              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#243949] mb-4">Exchange Performance</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {Object.entries(exchangeMetrics).map(([exchange, metrics]) => (
                    <ExchangeMetricsCard 
                      key={exchange} 
                      exchange={exchange} 
                      metrics={metrics} 
                    />
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h2 className="text-2xl font-semibold text-[#243949] mb-4">Strategy Details</h2>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">Strategies by Exchange</CardTitle>
                    <CardDescription>
                      Detailed metrics for each strategy across exchanges
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="all" className="w-full">
                      <TabsList className="mb-4">
                        <TabsTrigger value="all">All Exchanges</TabsTrigger>
                        {Object.keys(exchangeMetrics).map(exchange => (
                          <TabsTrigger key={exchange} value={exchange}>
                            {exchange}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      
                      <TabsContent value="all">
                        <StrategyTable data={data} />
                      </TabsContent>
                      
                      {Object.keys(exchangeMetrics).map(exchange => (
                        <TabsContent key={exchange} value={exchange}>
                          <StrategyTable 
                            data={data.filter(d => d.exchange.toLowerCase() === exchange.toLowerCase())}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </main>
      </div>
    </AdminRoute>
  );
};

export default MonitoringDashboard;
